import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { countByDay, formatLocalYmd, parseLocalYmd, weekdayNames, formatDayMonthYear } from "@heraion/shared";

import { usePlans } from "../Plans/usePlans";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { Button } from "../primitives/Button";
import { MonthCalendar } from "../primitives/MonthCalendar";
import { useLocale } from "../primitives/useLocale";
import type { DurableWriteFn } from "../seams";
import type { UiTheme } from "../theme";
import { useDateLocale } from "../primitives/DateFormatContext";

// #368: a plan's date, picked on the month (it replaced the two host date
// fields, the Android picker and the web's <input type=date>). The other
// planned sessions are marked, so the athlete sees what is already there.
// Past days cannot be picked, today can. The date stays optional: Clear.
export function PlanScheduleField({
  theme,
  value,
  onChange,
  apiFetch,
  durableWrite,
  planId,
}: {
  theme: UiTheme;
  /** "YYYY-MM-DD", or "" for none. */
  value: string;
  onChange: (ymd: string) => void;
  apiFetch: ProgramApiFetch;
  durableWrite: DurableWriteFn;
  /** The plan being edited: not a marker of its own. */
  planId?: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dateLocale = useDateLocale();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { plans } = usePlans({ apiFetch, durableWrite });
  const markers = useMemo(
    () => countByDay(plans.filter((p) => p.id !== planId).map((p) => p.scheduledFor)),
    [plans, planId],
  );
  const picked = parseLocalYmd(value);
  const words = picked
    ? `${weekdayNames(locale, 0)[picked.getDay()]} ${formatDayMonthYear(picked, dateLocale)}`
    : t("plans.noDate");
  return (
    <View style={styles.wrap}>
      <View style={styles.frame}>
        <MonthCalendar
          theme={theme}
          value={value}
          onChange={onChange}
          markers={markers}
          minDate={formatLocalYmd(new Date())}
        />
      </View>
      <View style={styles.pickedRow}>
        <Text style={[styles.picked, !picked && styles.unset]} accessibilityLiveRegion="polite">
          {words}
        </Text>
        {picked ? (
          <Button
            theme={theme}
            variant="ghost"
            compact
            label={t("plans.clear")}
            accessibilityLabel={t("plans.clearDate")}
            onPress={() => onChange("")}
          />
        ) : null}
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2 },
    frame: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      // No side padding: the seven columns keep every pixel of a narrow sheet.
      paddingVertical: s.s2,
    },
    pickedRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: s.s2 },
    picked: { flex: 1, color: p.ink, fontSize: ty.sizes.base, fontWeight: "600" },
    unset: { color: p.ink3, fontWeight: "400" },
  });
}
